import React, { useState, useEffect, useCallback } from 'react';
import {
  Home,
  Users,
  UserPlus,
  Package,
  Ship,
  DollarSign,
  BarChart3,
  Trash2,
  Settings,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

import { NotificacionesProvider } from './contexts/NotificacionesContext';
import { ToastProvider } from './components/ToastContainer';
import { AuthProvider } from './contexts/AuthContext';
import apiService from './services/api';
import Login from './components/Login';
import Dashboard from './pages/Dashboard';
import Clientes from './pages/Clientes';
import AltaCliente from './pages/AltaCliente';
import ClienteDetalle from './pages/ClienteDetalle';
import Contenedores from './pages/Contenedores';
import Navieras from './pages/Navieras';
import Tarifas from './pages/Tarifas';
import Reportes from './pages/Reportes';
import UsuariosPage from './pages/UsuariosPage';
import Papelera from './pages/Papelera';
import Ajustes from './pages/Ajustes';
import Navbar from './components/Navbar';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import InactivityWarning from './components/InactivityWarning';

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedClienteNombre, setSelectedClienteNombre] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [logoutMessage, setLogoutMessage] = useState(null);
  const [contenedorBuscado, setContenedorBuscado] = useState(null);
  const [inactividadMinutos, setInactividadMinutos] = useState(30); // default 30 min

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');

        if (!token || !storedUser) {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setUser(null);
          setIsLoading(false);
          return;
        }

        // Validar sesión con el servidor
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          // Usar datos del servidor, no del localStorage
          setUser(data.usuario || JSON.parse(storedUser));
        } else {
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          setUser(null);
        }
      } catch (error) {
        // Si el servidor no responde, limpiar sesión por seguridad
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Cargar timeout de inactividad desde la BD cuando hay usuario logueado
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/configuracion/publica`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : null)
      .then(config => {
        if (config?.inactividad_timeout) {
          setInactividadMinutos(config.inactividad_timeout);
        }
      })
      .catch(() => {}); // Silenciar — usar default
  }, [user]);

  // Actualizar timeout cuando se guarda config desde Ajustes
  useEffect(() => {
    const handleConfigUpdated = (e) => {
      if (e.detail?.inactividad_timeout) {
        setInactividadMinutos(e.detail.inactividad_timeout);
      }
    };
    window.addEventListener('config-updated', handleConfigUpdated);
    return () => window.removeEventListener('config-updated', handleConfigUpdated);
  }, []);

  // Configurar el callback de sesión expirada en el servicio API
  useEffect(() => {
    apiService.setOnUnauthorized(() => {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      setUser(null);
      setCurrentView('dashboard');
      setSelectedClienteNombre(null);
    });
  }, []);

  // Función de logout por inactividad
  const handleInactivityLogout = useCallback(() => {
    setLogoutMessage(
      `Tu sesión se cerró automáticamente después de ${inactividadMinutos} minuto${inactividadMinutos !== 1 ? 's' : ''} de inactividad. Por favor, inicia sesión nuevamente.`
    );
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setCurrentView('dashboard');
    setSelectedClienteNombre(null);
  }, [inactividadMinutos]);

  // Hook de inactividad — duración desde la BD
  const { showWarning, remainingSeconds } = useInactivityTimeout({
    onTimeout: handleInactivityLogout,
    timeoutDuration: inactividadMinutos * 60 * 1000,
    warningDuration: 60000,   // 60 segundos de advertencia (fijo)
    enabled: !!user
  });

  // Definición completa de todos los módulos con animaciones
  const allMenuItems = [
    { id: 'dashboard', name: 'Dashboard', icon: Home, animation: 'icon-bounce' },
    { id: 'clientes', name: 'Clientes', icon: Users, animation: 'icon-scale' },
    { id: 'alta-cliente', name: 'Alta Cliente', icon: UserPlus, animation: 'icon-swing' },
    { id: 'contenedores', name: 'Contenedores', icon: Package, animation: 'icon-shake' },
    { id: 'navieras', name: 'Navieras', icon: Ship, animation: 'icon-slide' },
    { id: 'tarifas', name: 'Tarifas', icon: DollarSign, animation: 'icon-bounce' },
    { id: 'reportes', name: 'Reportes', icon: BarChart3, animation: 'icon-grow' },
    { id: 'usuarios', name: 'Usuarios', icon: Users, animation: 'icon-scale' },
    { id: 'papelera', name: 'Papelera', icon: Trash2, animation: 'icon-shake' },
    { id: 'administracion', name: 'Ajustes', icon: Settings, animation: 'icon-rotate' }
  ];

  // Módulos restringidos solo para admin y superadmin
  const restrictedModules = ['usuarios', 'papelera', 'administracion'];

  // Filtrar menú según el rol del usuario
  const menuItems = allMenuItems.filter(item => {
    if (restrictedModules.includes(item.id)) {
      return user && (user.rol === 'admin' || user.rol === 'superadmin');
    }
    return true;
  });

  const handleLogin = (userData) => {
    setUser(userData);
    // Avisar al Context para que cargue config y muestre alertas activas
    window.dispatchEvent(new CustomEvent('user-login'));
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setCurrentView('dashboard');
    setSelectedClienteNombre(null);
  };

  const handleClienteClick = (clienteNombre) => {
    setSelectedClienteNombre(clienteNombre);
    setCurrentView('cliente-detalle');
  };

  const handleContenedorClick = (contenedor) => {
    setContenedorBuscado(contenedor);
    setCurrentView('contenedores');
  };

  const renderView = () => {
    // Protección contra acceso directo a módulos restringidos
    const restrictedModules = ['usuarios', 'papelera', 'administracion'];

    if (restrictedModules.includes(currentView)) {
      if (user && user.rol !== 'admin' && user.rol !== 'superadmin') {
        // Redirigir al dashboard si el usuario no tiene permisos
        setCurrentView('dashboard');
        return <Dashboard onClienteClick={handleClienteClick} />;
      }
    }

    switch (currentView) {
      case 'dashboard':
        return <Dashboard onClienteClick={handleClienteClick} />;
      case 'clientes':
        return <Clientes onClienteClick={handleClienteClick} />;
      case 'alta-cliente':
        return <AltaCliente />;
      case 'contenedores':
        return <Contenedores contenedorBuscado={contenedorBuscado} onContenedorVisto={() => setContenedorBuscado(null)} />;
      case 'navieras':
        return <Navieras />;
      case 'tarifas':
        return <Tarifas />;
      case 'reportes':
        return <Reportes />;
      case 'papelera':
        return <Papelera />;
      case 'administracion':
        return <Ajustes />;
      case 'usuarios':
        return <UsuariosPage />;
      case 'cliente-detalle':
        return <ClienteDetalle clienteNombre={selectedClienteNombre} onBack={() => setCurrentView('clientes')} />;
      default:
        return <Dashboard onClienteClick={handleClienteClick} />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-ink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-ink-200 border-t-ink-800 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-sm text-ink-400 tracking-wide">Cargando</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <ToastProvider>
        <Login
          onLogin={handleLogin}
          logoutMessage={logoutMessage}
          onClearMessage={() => setLogoutMessage(null)}
        />
      </ToastProvider>
    );
  }

  return (
    <AuthProvider onLogout={handleLogout}>
      <ToastProvider>
        <NotificacionesProvider>
          {/* Modal de advertencia de inactividad */}
          <InactivityWarning
            show={showWarning}
            remainingSeconds={remainingSeconds}
          />

          <div className="flex h-screen bg-ink-50 overflow-hidden">
          {/* Sidebar Minimalista */}
          <aside
            className={`
              ${isSidebarOpen ? 'w-56' : 'w-16'}
              bg-white border-r border-ink-100
              transition-all duration-300 ease-out
              flex flex-col
              relative
            `}
          >
            {/* Logo */}
            <div className="h-16 flex items-center justify-center border-b border-ink-100">
              {isSidebarOpen ? (
                <img
                  src="/images/LogotipoDragonCargoWhite.png"
                  alt="Dragon Cargo"
                  className="h-8 w-auto object-contain invert opacity-90"
                />
              ) : (
                <img
                  src="/images/apple-touch-icon.png"
                  alt="Dragon Cargo"
                  className="w-10 h-10 rounded-lg object-cover shadow-sm"
                />
              )}
            </div>

            {/* Navigation */}
            <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto scrollbar-hide">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;

                return (
                  <button
                    key={item.id}
                    onClick={() => setCurrentView(item.id)}
                    className={`
                      w-full flex items-center gap-3
                      ${isSidebarOpen ? 'px-3' : 'justify-center px-0'}
                      py-2.5 rounded-lg
                      transition-all duration-200
                      group relative
                      ${isActive
                        ? 'bg-ink-900 text-white'
                        : 'text-ink-500 hover:bg-ink-50 hover:text-ink-800'
                      }
                    `}
                    title={!isSidebarOpen ? item.name : undefined}
                  >
                    <div className={item.animation}>
                      <Icon
                        size={18}
                        strokeWidth={isActive ? 2 : 1.5}
                        className={isActive ? 'text-white' : 'text-ink-400 group-hover:text-ink-600'}
                      />
                    </div>
                    {isSidebarOpen && (
                      <span className="text-sm font-medium tracking-tight">
                        {item.name}
                      </span>
                    )}

                    {/* Tooltip para sidebar colapsado */}
                    {!isSidebarOpen && (
                      <div className="
                        absolute left-full ml-2 px-2 py-1
                        bg-ink-900 text-white text-xs rounded
                        opacity-0 group-hover:opacity-100
                        pointer-events-none
                        transition-opacity duration-200
                        whitespace-nowrap z-50
                      ">
                        {item.name}
                      </div>
                    )}
                  </button>
                );
              })}
            </nav>

            {/* Toggle Button */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="
                absolute -right-3 top-20
                w-6 h-6 bg-white border border-ink-200
                rounded-full shadow-soft
                flex items-center justify-center
                hover:bg-ink-50 hover:border-ink-300
                transition-all duration-200
                z-10
              "
            >
              {isSidebarOpen ? (
                <ChevronLeft size={12} className="text-ink-500" />
              ) : (
                <ChevronRight size={12} className="text-ink-500" />
              )}
            </button>

            {/* Footer */}
            {isSidebarOpen && (
              <div className="p-4 border-t border-ink-100">
                <p className="text-[10px] text-ink-300 uppercase tracking-widest">
                  Dragon Cargo
                </p>
              </div>
            )}
          </aside>

          {/* Main Content */}
          <main className="flex-1 overflow-hidden flex flex-col bg-ink-50">
            <Navbar user={user} onLogout={handleLogout} onClienteClick={handleClienteClick} onContenedorClick={handleContenedorClick} />

            <div className="flex-1 overflow-auto">
              {renderView()}
            </div>
          </main>
        </div>
      </NotificacionesProvider>
    </ToastProvider>
    </AuthProvider>
  );
}

export default App;
